import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";
/**
 * The generic `label: <value>` row with a copy affordance.
 *
 * LIVES HERE, NOT IN `error-card.tsx`, because the read path (`ErrorCard`) and the
 * write path (`MutationErrorAlert`) must render the identical affordance: the whole
 * point of issue #57 is that a user reporting a failed SAVE can quote the same kind
 * of id as a user reporting a failed LOAD. Two copies of this would drift.
 *
 * `CopyReferenceButton` below is a thin, backward-compatible wrapper over this for the
 * error-correlation call sites; any other caller with a label/value/copy pair (e.g. a
 * Settings "About" card's Tenant ID) uses this directly instead of hand-rolling a copy
 * button.
 */
/**
 * Offers a line-break opportunity after separators (dots, dashes, slashes, ...) so a long value
 * wraps at a natural point; `overflow-wrap:anywhere` on the element still breaks a token that has
 * none and cannot fit. `<wbr>` adds no character, so select-all and copy still yield the exact value.
 */
function breakableParts(value) {
    return value
        .split(/(?<=[._\-/:=&?@])/)
        .flatMap((part, index) => (index === 0 ? [part] : [_jsx("wbr", {}, index), part]));
}
export function CopyFieldButton({ field, className, stacked = false, }) {
    const [copied, setCopied] = useState(false);
    const [failed, setFailed] = useState(false);
    const handleCopy = () => {
        if (typeof navigator === "undefined" || navigator.clipboard === undefined) {
            setFailed(true);
            return;
        }
        navigator.clipboard
            .writeText(field.value)
            .then(() => {
            setFailed(false);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        })
            .catch(() => {
            // Clipboard access can be refused by the browser (permissions, insecure
            // context) — the value is still visible and selectable; the stacked layout
            // says so, the compact row has nothing to recover.
            setFailed(true);
        });
    };
    if (stacked) {
        return (_jsxs("div", { role: "group", "aria-label": field.label, className: cn("flex min-w-0 flex-col gap-1.5", className), children: [_jsx("span", { className: "text-sm font-medium", children: field.label }), _jsxs("div", { className: "flex min-w-0 items-start gap-2", children: [_jsx("code", { className: "bg-surface-raised border-border min-w-0 flex-1 select-all rounded-control border px-2 py-1.5 font-mono text-xs [overflow-wrap:anywhere]", children: breakableParts(field.value) }), _jsxs(Button, { type: "button", variant: "outline", size: "sm", onClick: handleCopy, "aria-label": field.copyLabel, children: [copied ? _jsx(Check, { size: 14, "aria-hidden": "true" }) : _jsx(Copy, { size: 14, "aria-hidden": "true" }), copied ? field.copiedLabel : (field.copyText ?? field.copyLabel)] })] }), _jsx("span", { role: "status", className: "text-xs text-muted empty:hidden", children: failed ? field.copyFailedLabel : copied ? field.copiedLabel : null })] }));
    }
    return (_jsxs("div", { className: cn("mt-2 flex items-center gap-1.5 text-xs text-muted", className), children: [_jsxs("span", { children: [field.label, ": ", _jsx("code", { className: "font-mono", children: field.value })] }), _jsx(Button, { type: "button", variant: "ghost", size: "icon", className: "size-6 min-h-0 min-w-0 p-0", onClick: handleCopy, "aria-label": copied ? field.copiedLabel : field.copyLabel, children: copied ? _jsx(Check, { size: 14, "aria-hidden": "true" }) : _jsx(Copy, { size: 14, "aria-hidden": "true" }) })] }));
}
/**
 * The error-correlation specialisation of {@link CopyFieldButton} — `Reference: <id>` and
 * a copy affordance. Kept as its own named export (rather than asking every error call
 * site to build a `CopyableField`) because `ErrorReference` is already threaded through
 * `FieldError`, `resolveErrorReference` and `toFieldError` by name.
 */
export function CopyReferenceButton({ reference, className, }) {
    return (_jsx(CopyFieldButton, { field: {
            value: reference.requestId,
            label: reference.label,
            copyLabel: reference.copyLabel,
            copiedLabel: reference.copiedLabel,
        }, className: className }));
}
/** Dev-only real error detail. See {@link ErrorDebugDetail}. */
export function DebugDetailBlock({ debug }) {
    return (_jsxs("div", { className: "bg-surface-raised border-border mt-2 w-full rounded-control border p-3 text-xs", children: [_jsxs("p", { className: "font-mono font-semibold text-danger", children: [debug.name, ": ", debug.message] }), debug.stack === undefined ? null : (_jsxs("details", { className: "mt-1", children: [_jsx("summary", { className: "cursor-pointer select-none text-muted", children: debug.stackTraceLabel }), _jsx("pre", { className: "mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-muted", children: debug.stack })] }))] }));
}
//# sourceMappingURL=error-reference.js.map